/**
 * Done. / ChiroCPD — ErrorSummary Primitive
 *
 * Accessible form error summary. Appears at top of form on failed submission.
 * role="alert" ensures immediate announcement. tabindex="-1" allows programmatic focus.
 * Each error links to the offending field.
 */

import { useEffect, useRef } from 'react';
import type { FormError } from '@/lib/types';

interface ErrorSummaryProps {
  errors: FormError[];
  heading?: string;
}

export function ErrorSummary({ errors, heading = 'Please fix the following errors:' }: ErrorSummaryProps): React.ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);

  // Move focus to the error summary when it appears
  useEffect(() => {
    if (errors.length > 0) {
      ref.current?.focus();
    }
  }, [errors.length]);

  if (errors.length === 0) return null;

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      className="rounded-[var(--radius-md)] border border-[var(--color-error-500)] bg-[var(--color-error-100)] px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-error-500)]"
    >
      <h2 className="text-sm font-semibold text-red-800">{heading}</h2>
      <ul className="mt-2 list-disc pl-5 space-y-1">
        {errors.map(err => (
          <li key={err.field} className="text-sm text-red-700">
            <a
              href={`#${err.field}`}
              className="underline hover:no-underline focus-visible:ring-2 focus-visible:ring-red-500"
            >
              {err.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
